// src/App.tsx
import { KindeProvider } from "@kinde-oss/kinde-auth-react";
import { useGTM } from "gtm-react-hook";
import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "react-hot-toast";
import { Provider } from "react-redux";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { AbilityProvider, ability } from "./casl";
import { useKindeTokenSync } from "./hooks/useKindeTokenSync";
import { PermissionProvider } from "./permissions";
import { store } from "./redux/store";
import { routes } from "./routes";

const rawBase = import.meta.env.VITE_RIDEMINDER_BASE_NAME || "";
// Normalize basename (no leading/trailing slash), then add leading slash when present
const basename = rawBase.replace(/^\/|\/$/g, "");
const basePrefix = basename ? `/${basename}` : "";

// We will land on /auth-callback (your callback page) after auth.
// Make sure this exact URL is in Kinde's Allowed Redirect URLs.
const DEFAULT_REDIRECT_URI = (import.meta.env.VITE_KINDE_REDIRECT_URI as string) || `${window.location.origin}${basePrefix}/auth-callback`;

const DEFAULT_LOGOUT_URI = (import.meta.env.VITE_KINDE_LOGOUT_REDIRECT_URI as string) || `${window.location.origin}${basePrefix}/login`;

/**
 * Inner component that syncs Kinde auth with Redux
 * Must be inside both KindeProvider and Redux Provider
 */
function AppContent() {
  // Sync Kinde authentication state with Redux and initialize token provider
  useKindeTokenSync();

  // Router created once. basename keeps routing stable under subpaths.
  const router = useMemo(() => createBrowserRouter(routes, { basename: basePrefix }), []);

  const { runGTM } = useGTM();
  useEffect(() => {
    const rideminderGTM = import.meta.env.VITE_RIDEMINDER_GOOGLE_TAG_MANAGER_ID;
    const operatorGTM = window.rm?.config?.site?.ga_property;
    if (rideminderGTM) runGTM({ tagId: rideminderGTM });
    if (operatorGTM) runGTM({ tagId: operatorGTM });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PermissionProvider>
        <AbilityProvider value={ability}>
          <RouterProvider router={router} />
        </AbilityProvider>
      </PermissionProvider>
      {createPortal(<Toaster />, document.body)}
    </>
  );
}

export default function App() {
  return (
    <KindeProvider
      clientId={import.meta.env.VITE_KINDE_CLIENT_ID as string}
      domain={import.meta.env.VITE_KINDE_DOMAIN as string}
      redirectUri={DEFAULT_REDIRECT_URI}
      logoutUri={DEFAULT_LOGOUT_URI}
    >
      <HelmetProvider>
        <Provider store={store}>
          <AppContent />
        </Provider>
      </HelmetProvider>
    </KindeProvider>
  );
}
